import { createStore, select, setProp, withProps } from '@ngneat/elf';
import { Injectable, inject } from '@angular/core';
import { localStorageStrategy, persistState } from '@ngneat/elf-persist-state';
import {
  getActiveEntity,
  getAllEntitiesApply,
  selectActiveEntity,
  selectAllEntities,
  setActiveId,
  setEntities,
  withActiveId,
  withEntities,
} from '@ngneat/elf-entities';
import {
  ILanguage,
  ITranslationLanguage,
} from '@app-core/interfaces/language.interface';
import { Observable, map, shareReplay } from 'rxjs';
import {
  LanguageCode,
  LanguageCodes,
} from '@app-shared/enums/language-codes.enum';
import { EmailLanguageType } from '@app-core/interfaces/preferred-email-language.interface';
import { NGQ_LANGUAGES_TOKEN } from '@app-core/injection-tokens/languages.token';

export interface LanguageProps {
  hasUserChosenLanguage: boolean;
}

const initialProps: LanguageProps = {
  hasUserChosenLanguage: false,
};

const STORE_NAME = 'sandoz-v2-languages';

@Injectable({ providedIn: 'root' })
export class LanguageRepository {
  private readonly languagesList =
    inject<ITranslationLanguage[]>(NGQ_LANGUAGES_TOKEN);

  private readonly store;

  public readonly languages$: Observable<ILanguage[]>;
  public readonly activeLanguage$: Observable<ILanguage | undefined>;
  public readonly activeLanguageCode$: Observable<LanguageCodes | null>;

  public readonly hasUserChosenLanguage$: Observable<boolean>;
  public readonly appLanguage$: Observable<ITranslationLanguage | undefined>;

  constructor() {
    this.store = createStore(
      { name: STORE_NAME },
      withEntities<ILanguage>(),
      withActiveId(),
      withProps<LanguageProps>(initialProps),
    );

    persistState(this.store, {
      key: STORE_NAME,
      storage: localStorageStrategy,
    });

    this.languages$ = this.store.pipe(
      selectAllEntities(),
      shareReplay({ refCount: true, bufferSize: 1 }),
    );

    this.activeLanguage$ = this.store.pipe(
      selectActiveEntity(),
      shareReplay({ refCount: true, bufferSize: 1 }),
    );

    this.activeLanguageCode$ = this.activeLanguage$.pipe(
      map(l => l?.short_code || null),
      shareReplay({ refCount: true, bufferSize: 1 }),
    );

    this.hasUserChosenLanguage$ = this.store.pipe(
      select(state => state.hasUserChosenLanguage),
      shareReplay({ refCount: true, bufferSize: 1 }),
    );

    this.appLanguage$ = this.activeLanguage$.pipe(
      map(active => {
        return this.languagesList.find(lang => lang.id === active?.short_code);
      }),
      shareReplay({ refCount: true, bufferSize: 1 }),
    );
  }

  setLanguages(languages: ILanguage[]): void {
    this.store.update(setEntities(languages));
  }

  /**
   * Gets the currently active language code or `EN_GB` if one is not saved.
   */
  getActiveLanguageCodeOrDefaultCode(): LanguageCodes {
    return (
      this.store.query(getActiveEntity())?.short_code || LanguageCode.EN_GB
    );
  }

  getActiveLanguageCode(): LanguageCodes | null {
    return this.store.query(getActiveEntity())?.short_code || null;
  }

  getLanguageByCode(code: LanguageCodes): ILanguage | null {
    const matches: ILanguage[] = this.store.query(
      getAllEntitiesApply({
        filterEntity: e => e.short_code === code,
      }),
    );

    return matches.length ? matches[0] : null;
  }

  /**
   * Set the active language by its code.
   *
   * @param code
   */
  setActiveLanguageByCode(code: LanguageCodes): void {
    const lang = this.getLanguageByCode(code);
    this.store.update(setActiveId(lang?.id || null));
  }

  setActiveLanguage(lang: ILanguage): void {
    this.store.update(setActiveId(lang.id));
  }

  setHasUserChosenLanguage(
    value: LanguageProps['hasUserChosenLanguage'],
  ): void {
    this.store.update(setProp('hasUserChosenLanguage', value));
  }

  /**
   * Gets the language using EmailLanguageType from the BE as a parameter
   *
   * @param code - user.email_language example: 'cs' for Czech
   * @returns ILanguage
   */
  getLanguageByBackendCode(code: EmailLanguageType): ILanguage | null {
    const matches = this.store.query(
      getAllEntitiesApply({
        filterEntity: entity => {
          const emailLang = entity.short_code.toLowerCase().split('-');

          return emailLang[1] === code || emailLang[0] === code;
        },
      }),
    );

    return matches.length ? matches[0] : null;
  }
}
